import gsap from 'gsap';

export interface TweenConfig {
  targets: object | object[];
  duration?: number;
  delay?: number;
  ease?: string;
  repeat?: number;
  yoyo?: boolean;
  onComplete?: () => void;
  onUpdate?: () => void;
  onStart?: () => void;
  [key: string]: unknown;
}

// Map ease names to GSAP ease names
const EASE_MAP: Record<string, string> = {
  // Common ease names -> GSAP
  Linear: 'none',
  'Linear.easeNone': 'none',
  'Quad.easeIn': 'power1.in',
  'Quad.easeOut': 'power1.out',
  'Quad.easeInOut': 'power1.inOut',
  'Cubic.easeIn': 'power2.in',
  'Cubic.easeOut': 'power2.out',
  'Cubic.easeInOut': 'power2.inOut',
  'Quart.easeIn': 'power3.in',
  'Quart.easeOut': 'power3.out',
  'Quart.easeInOut': 'power3.inOut',
  'Quint.easeIn': 'power4.in',
  'Quint.easeOut': 'power4.out',
  'Quint.easeInOut': 'power4.inOut',
  'Sine.easeIn': 'sine.in',
  'Sine.easeOut': 'sine.out',
  'Sine.easeInOut': 'sine.inOut',
  'Expo.easeIn': 'expo.in',
  'Expo.easeOut': 'expo.out',
  'Expo.easeInOut': 'expo.inOut',
  'Circ.easeIn': 'circ.in',
  'Circ.easeOut': 'circ.out',
  'Circ.easeInOut': 'circ.inOut',
  'Elastic.easeIn': 'elastic.in',
  'Elastic.easeOut': 'elastic.out',
  'Elastic.easeInOut': 'elastic.inOut',
  'Back.easeIn': 'back.in',
  'Back.easeOut': 'back.out',
  'Back.easeInOut': 'back.inOut',
  'Bounce.easeIn': 'bounce.in',
  'Bounce.easeOut': 'bounce.out',
  'Bounce.easeInOut': 'bounce.inOut',
  // Common shorthand names
  Power0: 'power0',
  Power1: 'power1.out',
  Power2: 'power2.out',
  Power3: 'power3.out',
  Power4: 'power4.out',
};

/**
 * Convert ease name to GSAP ease name
 */
function convertEase(ease?: string): string {
  if (!ease) return 'power1.out';
  return EASE_MAP[ease] || ease;
}

/**
 * Tween wrapper using GSAP.
 */
export class Tween {
  private tween: gsap.core.Tween;

  constructor(tween: gsap.core.Tween) {
    this.tween = tween;
  }

  /**
   * Stop the tween
   */
  stop(): this {
    this.tween.kill();
    return this;
  }

  /**
   * Pause the tween
   */
  pause(): this {
    this.tween.pause();
    return this;
  }

  /**
   * Resume the tween
   */
  resume(): this {
    this.tween.resume();
    return this;
  }

  /**
   * Check if tween is playing
   */
  isPlaying(): boolean {
    return this.tween.isActive();
  }

  /**
   * Get the underlying GSAP tween
   */
  get gsapTween(): gsap.core.Tween {
    return this.tween;
  }
}

/**
 * Tween manager using GSAP.
 */
export class TweenManager {
  private tweens: Set<Tween> = new Set();

  /**
   * Create and start a tween
   */
  add(config: TweenConfig): Tween {
    const { targets, duration, delay, ease, repeat, yoyo, onComplete, onUpdate, onStart, ...props } = config;

    // Convert duration (ms) to GSAP duration (seconds)
    const durationSeconds = (duration ?? 1000) / 1000;
    const delaySeconds = (delay ?? 0) / 1000;

    const gsapConfig: gsap.TweenVars = {
      ...props,
      duration: durationSeconds,
      delay: delaySeconds,
      ease: convertEase(ease),
      repeat: repeat ?? 0,
      yoyo: yoyo ?? false,
      onComplete: () => {
        if (onComplete) onComplete();
        this.tweens.delete(tween);
      },
      onUpdate,
      onStart,
    };

    const gsapTween = gsap.to(targets, gsapConfig);
    const tween = new Tween(gsapTween);
    this.tweens.add(tween);

    return tween;
  }

  /**
   * Create a tween from one value to another
   */
  addFrom(config: TweenConfig): Tween {
    const { targets, duration, delay, ease, repeat, yoyo, onComplete, onUpdate, onStart, ...props } = config;

    const durationSeconds = (duration ?? 1000) / 1000;
    const delaySeconds = (delay ?? 0) / 1000;

    const gsapConfig: gsap.TweenVars = {
      ...props,
      duration: durationSeconds,
      delay: delaySeconds,
      ease: convertEase(ease),
      repeat: repeat ?? 0,
      yoyo: yoyo ?? false,
      onComplete: () => {
        if (onComplete) onComplete();
        this.tweens.delete(tween);
      },
      onUpdate,
      onStart,
    };

    const gsapTween = gsap.from(targets, gsapConfig);
    const tween = new Tween(gsapTween);
    this.tweens.add(tween);

    return tween;
  }

  /**
   * Create a timeline (for chained/sequenced animations)
   */
  createTimeline(): gsap.core.Timeline {
    return gsap.timeline();
  }

  /**
   * Kill all tweens on a target
   */
  killTweensOf(target: object): void {
    gsap.killTweensOf(target);
  }

  /**
   * Stop all tweens
   */
  killAll(): void {
    for (const tween of this.tweens) {
      tween.stop();
    }
    this.tweens.clear();
  }

  /**
   * Pause all tweens
   */
  pauseAll(): void {
    for (const tween of this.tweens) {
      tween.pause();
    }
  }

  /**
   * Resume all tweens
   */
  resumeAll(): void {
    for (const tween of this.tweens) {
      tween.resume();
    }
  }

  /**
   * Get count of active tweens
   */
  get activeTweenCount(): number {
    return this.tweens.size;
  }
}

// Global tween manager instance
export const tweens = new TweenManager();

/**
 * Helper function for simple tweens (can be used directly without TweenManager)
 */
export function tween(config: TweenConfig): Tween {
  return tweens.add(config);
}

/**
 * Helper for delayed callbacks
 */
export function delay(ms: number, callback: () => void): gsap.core.Tween {
  return gsap.delayedCall(ms / 1000, callback);
}

/**
 * Cancel a delayed call
 */
export function cancelDelay(tween: gsap.core.Tween): void {
  tween.kill();
}
